// ─────────────────────────────────────────────────────────────────────────────
//  platform/config.js — Environment-only configuration (Amendment A12)
//
//  No secret, URL or key may live in source. Everything comes from environment
//  variables. There are NO fallback values: a missing required variable fails
//  loudly instead of silently pointing a build at the wrong project.
//
//  Access is lazy (getters), so importing this module never throws; a value is
//  validated only when it is actually read.
// ─────────────────────────────────────────────────────────────────────────────
import { AppError } from './errors';

// Explicit, STATICALLY-referenced env mapping.
// Create React App / webpack only reliably inline `process.env.REACT_APP_*` when
// each variable is referenced statically. Reading `process.env[name]` dynamically
// is bundler-dependent, so we capture the values here with static references and
// look everything up in this object instead of touching process.env dynamically.
const browserEnv = {
  REACT_APP_SUPABASE_URL: process.env.REACT_APP_SUPABASE_URL,
  REACT_APP_SUPABASE_ANON_KEY: process.env.REACT_APP_SUPABASE_ANON_KEY,
  REACT_APP_BACKEND_URL: process.env.REACT_APP_BACKEND_URL,
  REACT_APP_API_BASE: process.env.REACT_APP_API_BASE,
  REACT_APP_CLICKPESA_CHECKOUT_URL: process.env.REACT_APP_CLICKPESA_CHECKOUT_URL,
  REACT_APP_DEV_MODE: process.env.REACT_APP_DEV_MODE,
};

const env = () => browserEnv;

// Read a required variable or throw. Trims and rejects empty strings.
export function requireEnv(name, source = env()) {
  const v = source[name];
  if (v === undefined || v === null || String(v).trim() === '') {
    throw new AppError('ERR_INTERNAL', `Missing required environment variable: ${name}`);
  }
  return String(v);
}

// Read an optional variable with an explicit fallback (used for non-secret flags).
export function optionalEnv(name, fallback = '', source = env()) {
  const v = source[name];
  return v === undefined || v === null ? fallback : String(v);
}

// The variables the application cannot run without.
export const REQUIRED_ENV = Object.freeze([
  'REACT_APP_SUPABASE_URL',
  'REACT_APP_SUPABASE_ANON_KEY',
  'REACT_APP_BACKEND_URL',
  'REACT_APP_API_BASE',
]);

// Validate all required variables at once (used by CI / boot checks).
export function assertConfig(source = env()) {
  REQUIRED_ENV.forEach((k) => requireEnv(k, source));
  return true;
}

// Central, lazily-validated configuration surface. Consumers read config.X;
// the getter validates the underlying env var at access time.
export const config = {
  get SUPABASE_URL() { return requireEnv('REACT_APP_SUPABASE_URL'); },
  get SUPABASE_ANON_KEY() { return requireEnv('REACT_APP_SUPABASE_ANON_KEY'); },
  get BACKEND_URL() { return requireEnv('REACT_APP_BACKEND_URL'); },
  get API_BASE() { return requireEnv('REACT_APP_API_BASE'); },
  get CLICKPESA_CHECKOUT_URL() { return optionalEnv('REACT_APP_CLICKPESA_CHECKOUT_URL', ''); },
  get DEV_MODE() { return optionalEnv('REACT_APP_DEV_MODE', '') === 'true'; },
};
